import { useState, useEffect } from 'react';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row, Button } from 'reactstrap';
import { motion, AnimatePresence } from "framer-motion";
import axios from 'axios';

import DefaultNoUser from "../img/NoUserFound.png"; // default image

const NoDataFound2 = ({ style, t, Nouser, username, category, timestamp  }) => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshAttempts, setRefreshAttempts] = useState(0);
    const [cachedImage, setCachedImage] = useState(null);
    const [snackbar, setSnackbar] = useState({
        show: false,
        message: '',
        type: 'error'
    });

    // Cache image to localStorage when Nouser prop changes
    useEffect(() => {
        if (Nouser) {
            try {
                // Store the image URL in localStorage
                localStorage.setItem('cachedNoUserImage', Nouser);
                setCachedImage(Nouser);
            } catch (error) {
                console.error('Failed to cache image:', error);
            }
        } else {
            // Try to retrieve cached image from localStorage
            try {
                const cached = localStorage.getItem('cachedNoUserImage');
                if (cached) {
                    setCachedImage(cached);
                }
            } catch (error) {
                console.error('Failed to retrieve cached image:', error);
            }
        }
    }, [Nouser]);

    const showSnackbar = (message, type = 'error') => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => {
            setSnackbar({ show: false, message: '', type: 'error' });
        }, 1000);
    };

    const checkInternetConnection = async () => {
        // First check navigator.onLine
        if (!navigator.onLine) {
            return false;
        }
        // Double-check with a real network request
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            console.log('Network check failed:', error);
            return false;
        }
    };

    const handleRefresh = async () => {
        setRefreshAttempts(prev => prev + 1);
        setIsRefreshing(true);
        const isOnline = await checkInternetConnection();
        if (!isOnline) {
            setSnackbar({
                show: true,
                message: t?.nointerDes || "No internet connection! Please try again later.",
                type: 'error'
            });
            
            setIsRefreshing(false);

            // Auto-hide after 3 seconds
            setTimeout(() => {
                setSnackbar({ show: false, message: '', type: 'error' });
            }, 3000);
            return;
        }

        setIsRefreshing(true);
        try {
            const deviceIp = localStorage.getItem("deviceIp");

            const response = await axios.post('https://api.lolcards.link/api/web/catgory/found', {
                username,
                category,
                timestamp,
                ip: deviceIp
            });

            if (response.status === 200 || response.data) {
                window.location.reload();
            }

        } catch (error) {
            const errorMessage = t?.description || 'Failed to refresh data. Please try again.';
            showSnackbar(errorMessage, 'error');
        } finally {
            setIsRefreshing(false);
        }
    };

    const shouldShowButton = refreshAttempts < 4;

    // Determine which image to display (priority: Nouser prop > cached > default)
    const displayImage = Nouser || cachedImage || DefaultNoUser;

    return (
        <div style={style} className='error-color'>
            <Row className='d-flex justify-content-center align-items-center m-0 page1-bg-90vh'>
                <Col sm={9} xl={5} className='h-100 d-flex flex-column justify-content-evenly align-items-center'>
                    <div className='d-flex flex-column justify-content-center align-items-center'>
                        <div className='pb-5'>
                            <img
                                src={displayImage}
                                alt='NoDataFound'
                                className='img-fluid'
                                width={250}
                                onError={(e) => {
                                    // Fallback to default image if cached image fails to load
                                    e.target.src = DefaultNoUser;
                                }}
                            />
                        </div>
                        <p className='fw-bold fs-5 m-0 error-color-title'>{t.title}</p>
                        <p className='m-0 pt-0 text-center error-color-des' style={{ whiteSpace: "pre-line" }}>
                            {t.description}
                        </p>
                    </div>

                    {shouldShowButton && (
                        <Button
                            className="mt-4 border-0"
                            onClick={handleRefresh}
                            disabled={isRefreshing || snackbar.show}
                            style={{
                                background: (isRefreshing || snackbar.show) ? "#cccccc" : "#FC674A",
                                width: "50px",
                                height: "50px",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                                cursor: (isRefreshing || snackbar.show) ? "not-allowed" : "pointer",
                                opacity: (isRefreshing || snackbar.show) ? 0.6 : 1,
                                transition: "all 0.3s ease"
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faArrowsRotate}
                                className={isRefreshing ? 'fa-spin' : ''}
                                style={{ fontSize: "18px", color: "white" }}
                            />
                        </Button>
                    )}

                </Col>
            </Row>

            <AnimatePresence>
                {snackbar.show && (
                    <motion.div
                        className={`snackbar d-flex align-item-center gap-3 ${snackbar.type}`}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    >
                        <div className="snackbar-icon px-3">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="9" stroke="#fff" strokeWidth="2" fill="none" />
                                <path d="M10 6V11" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                <circle cx="10" cy="14" r="1" fill="#fff" />
                            </svg>
                        </div>
                        <div className="snackbar-message">
                            {snackbar.message}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NoDataFound2;